<template>
  <div>
    <header class="site-header">
      <div class="header-shell">
        <div class="edge-icon-group edge-left">
          <button
            ref="searchToggleRef"
            class="edge-icon-button"
            type="button"
            :aria-expanded="String(searchOpen)"
            aria-controls="searchPanel"
            aria-label="打开搜索"
            title="搜索"
            @click.stop="toggleSearch"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M10.5 4a6.5 6.5 0 1 0 4.058 11.58l4.431 4.431 1.414-1.414-4.431-4.431A6.5 6.5 0 0 0 10.5 4zm0 2a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9z" />
            </svg>
          </button>

          <RouterLink
            class="edge-icon-button"
            to="/"
            aria-label="返回首页"
            title="返回首页"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3.2 3.5 10v10h6v-5.5h5V20h6V10L12 3.2zm0 2.56 6.5 5.2V18h-2v-5.5h-9V18h-2v-7.04l6.5-5.2z" />
            </svg>
          </RouterLink>
        </div>

        <div ref="headerCoreRef" class="header-core">
          <nav class="nav-cluster nav-left" aria-label="主导航左侧">
            <div
              v-for="group in navGroups.left"
              :key="group.id"
              class="nav-item"
              :class="{ 'is-open': openNavKey === group.id }"
            >
              <button class="nav-trigger" type="button" @click="toggleNav(group.id)">
                <span class="nav-label nav-label-full">{{ group.full }}</span>
                <span class="nav-label nav-label-short">{{ group.short }}</span>
                <span class="nav-caret"></span>
              </button>
              <div class="nav-dropdown">
                <a
                  v-for="link in group.links"
                  :key="link.id"
                  :href="link.href"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ link.text }}
                </a>
              </div>
            </div>
          </nav>

          <RouterLink class="brand-notch" to="/" aria-label="圣域空间首页">
            <div class="brand-badge brand-badge-full">
              <img src="/images/logo3.webp" alt="圣域空间 Logo">
            </div>
          </RouterLink>

          <nav class="nav-cluster nav-right" aria-label="主导航右侧">
            <div
              v-for="group in navGroups.right"
              :key="group.id"
              class="nav-item"
              :class="{ 'is-open': openNavKey === group.id }"
            >
              <button class="nav-trigger" type="button" @click="toggleNav(group.id)">
                <span class="nav-label nav-label-full">{{ group.full }}</span>
                <span class="nav-label nav-label-short">{{ group.short }}</span>
                <span class="nav-caret"></span>
              </button>
              <div class="nav-dropdown">
                <a
                  v-for="link in group.links"
                  :key="link.id"
                  :href="link.href"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ link.text }}
                </a>
              </div>
            </div>
          </nav>
        </div>

        <button
          class="header-side-logo"
          type="button"
          :aria-pressed="String(backdropPaused)"
          :aria-label="motionLabel"
          :title="motionTitle"
          :class="{ 'is-paused': backdropPaused }"
          @click="toggleMotion"
        >
          <img src="/images/logo4.webp" alt="" aria-hidden="true">
        </button>

        <button
          ref="userToggleRef"
          class="edge-icon-button edge-right"
          type="button"
          :aria-label="isLoggedIn ? '账户菜单' : '登录账户'"
          :title="isLoggedIn ? '账户菜单' : '登录账户'"
          :aria-expanded="String(userMenuOpen)"
          @click.stop="toggleUserMenu"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm0 10c-4.338 0-8 2.164-8 5v1h16v-1c0-2.836-3.662-5-8-5zm0 2c3.137 0 5.7 1.274 5.963 3H6.037c.263-1.726 2.826-3 5.963-3z" />
          </svg>
        </button>
      </div>

      <div
        id="searchPanel"
        ref="searchPanelRef"
        class="search-row"
        :hidden="!searchOpen"
        @click.stop
        @pointerdown.stop
      >
        <div class="search-row-inner">
          <div class="search-box">
            <input
              ref="searchInputRef"
              v-model.trim="searchTerm"
              type="search"
              placeholder="搜索全站内容，比如：教程、音频、Prompt、素材..."
              autocomplete="off"
              @keydown.enter.prevent="submitSearch"
            >
            <button
              type="button"
              :disabled="!filteredSearchItems.length"
              @click="submitSearch"
            >
              搜索
            </button>
          </div>
          <div class="search-results">
            <p v-if="!searchKeyword" class="search-hint">
              输入关键词后，这里会显示匹配到的站内入口与内容。
            </p>
            <p v-else-if="!filteredSearchItems.length" class="search-empty">
              没有找到和“{{ searchKeyword }}”相关的内容。
            </p>
            <template v-else>
              <p class="search-count">
                显示全部 {{ filteredSearchItems.length }} 条匹配的公开内容，已按匹配度排序。
              </p>
              <template v-for="item in filteredSearchItems" :key="resultKey(item)">
                <RouterLink
                  v-if="item.type === 'route'"
                  class="search-result-item"
                  :to="item.to"
                  @click="closeSearch"
                >
                  <strong v-html="highlightText(item.title)"></strong>
                  <span class="search-result-meta">{{ item.category }}</span>
                  <span
                    class="search-result-snippet"
                    v-html="highlightSnippet(item)"
                  ></span>
                </RouterLink>
                <a
                  v-else
                  class="search-result-item"
                  :href="item.href"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click="closeSearch"
                >
                  <strong v-html="highlightText(item.title)"></strong>
                  <span class="search-result-meta">{{ item.category }}</span>
                  <span
                    class="search-result-snippet"
                    v-html="highlightSnippet(item)"
                  ></span>
                </a>
              </template>
            </template>
          </div>
        </div>
      </div>

      <div
        ref="userPanelRef"
        class="user-panel"
        :hidden="!userMenuOpen"
        @click.stop
        @pointerdown.stop
      >
        <div class="user-panel-inner">
          <template v-if="!isLoggedIn">
            <div class="user-panel-head">
              <strong>登录后台账户</strong>
              <span>登录后按权限显示后台入口</span>
            </div>

            <label class="user-field">
              <span>用户名</span>
              <input v-model.trim="loginUsername" type="text" autocomplete="username">
            </label>

            <label class="user-field">
              <span>密码</span>
              <input
                v-model="loginPassword"
                :type="loginPasswordVisible ? 'text' : 'password'"
                autocomplete="current-password"
                @keydown.enter.prevent="submitLogin"
              >
              <label class="user-password-check">
                <input v-model="loginPasswordVisible" type="checkbox">
                <span>{{ loginPasswordVisible ? "隐藏密码" : "显示密码" }}</span>
              </label>
            </label>

            <p v-if="loginError" class="user-feedback is-error">{{ loginError }}</p>

            <button type="button" class="user-primary-button" @click="submitLogin">
              登录
            </button>
          </template>

          <template v-else>
            <div class="user-panel-head">
              <strong>{{ currentUser.displayName }}</strong>
              <span>@{{ currentUser.username }}</span>
            </div>

            <div class="user-badges">
              <span class="user-badge" :class="{ 'is-active': canAccessAdmin }">
                {{ canAccessAdmin ? "可访问后台" : "无后台权限" }}
              </span>
              <span v-if="isSuperAdmin" class="user-badge is-super">超级管理员</span>
            </div>

            <RouterLink
              v-if="canAccessAdmin"
              class="user-link-button"
              to="/admin"
              @click="closeUserMenu"
            >
              进入后台管理
            </RouterLink>

            <button
              type="button"
              class="user-secondary-button"
              @click="passwordEditorOpen = !passwordEditorOpen"
            >
              {{ passwordEditorOpen ? "收起修改密码" : "修改密码" }}
            </button>

            <div v-if="passwordEditorOpen" class="user-password-box">
              <label class="user-field">
                <span>当前密码</span>
                <input
                  v-model="currentPassword"
                  type="password"
                  autocomplete="current-password"
                >
              </label>
              <label class="user-field">
                <span>新密码</span>
                <input
                  v-model="nextPassword"
                  type="password"
                  autocomplete="new-password"
                >
              </label>
              <label class="user-field">
                <span>确认新密码</span>
                <input
                  v-model="confirmPassword"
                  type="password"
                  autocomplete="new-password"
                  @keydown.enter.prevent="submitPasswordChange"
                >
              </label>

              <p v-if="passwordMessage" class="user-feedback" :class="{ 'is-error': passwordError }">
                {{ passwordMessage }}
              </p>

              <button type="button" class="user-primary-button" @click="submitPasswordChange">
                保存新密码
              </button>
            </div>

            <button type="button" class="user-danger-button" @click="handleLogout">
              退出登录
            </button>
          </template>
        </div>
      </div>
    </header>

    <button
      v-if="isCompactSidebarViewport"
      ref="sidebarToggleRef"
      class="sidebar-compact-toggle"
      type="button"
      :aria-expanded="String(!sidebarCollapsed)"
      :aria-label="sidebarCollapsed ? '展开侧边栏' : '收起侧边栏'"
      :title="sidebarCollapsed ? '展开侧边栏' : '收起侧边栏'"
      @click.stop="toggleSidebar"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          v-if="sidebarCollapsed"
          d="M9.29 6.29a1 1 0 0 1 1.42 0l5 5a1 1 0 0 1 0 1.42l-5 5a1 1 0 1 1-1.42-1.42L13.59 12 9.29 7.71a1 1 0 0 1 0-1.42z"
        />
        <path
          v-else
          d="M14.71 6.29a1 1 0 0 1 0 1.42L10.41 12l4.3 4.29a1 1 0 0 1-1.42 1.42l-5-5a1 1 0 0 1 0-1.42l5-5a1 1 0 0 1 1.42 0z"
        />
      </svg>
    </button>

    <aside
      ref="responsiveSidebarRef"
      class="responsive-sidebar"
      :class="{ 'is-hidden': sidebarCollapsed }"
      aria-label="侧边导航"
    >
      <div class="responsive-sidebar-inner">
        <div class="responsive-sidebar-menu">
          <section
            v-for="group in sidebarGroups"
            :key="group.id"
            class="side-nav-group"
            :class="{ 'is-open': openSidebarGroupKey === group.id }"
            @mouseenter="openSidebarLinks(group.id)"
            @mouseleave="closeSidebarLinks(group.id)"
          >
            <button
              class="side-nav-trigger"
              type="button"
              :aria-expanded="String(openSidebarGroupKey === group.id)"
              @focus="openSidebarLinks(group.id)"
              @click="toggleSidebarGroup(group.id)"
            >
              <span class="side-nav-label-full">{{ group.full }}</span>
              <span class="side-nav-label-short">{{ group.short }}</span>
              <span class="side-nav-caret"></span>
            </button>
            <div class="side-nav-links">
              <div class="side-nav-links-title">{{ group.full }}</div>
              <a
                v-for="link in group.links"
                :key="link.id"
                class="side-nav-link"
                :href="link.href"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ link.text }}
              </a>
            </div>
          </section>
        </div>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/store/authStore";
import { useContentStore } from "@/store/contentStore";

const route = useRoute();
const router = useRouter();
const { navGroups, searchItems, loadContent } = useContentStore();
const {
  currentUser,
  isLoggedIn,
  canAccessAdmin,
  isSuperAdmin,
  login,
  logout,
  changeOwnPassword,
  ensureSessionLoaded,
} = useAuthStore();

const headerCoreRef = ref(null);
const searchToggleRef = ref(null);
const searchPanelRef = ref(null);
const searchInputRef = ref(null);
const userToggleRef = ref(null);
const userPanelRef = ref(null);
const sidebarToggleRef = ref(null);
const responsiveSidebarRef = ref(null);
const searchOpen = ref(false);
const userMenuOpen = ref(false);
const searchTerm = ref("");
const backdropPaused = ref(false);
const openNavKey = ref(null);
const openSidebarGroupKey = ref(null);
const isCompactSidebarViewport = ref(
  typeof window !== "undefined" ? window.innerWidth <= 1360 : false
);
const sidebarCollapsed = ref(isCompactSidebarViewport.value);
const loginUsername = ref("");
const loginPassword = ref("");
const loginPasswordVisible = ref(false);
const loginError = ref("");
const passwordEditorOpen = ref(false);
const currentPassword = ref("");
const nextPassword = ref("");
const confirmPassword = ref("");
const passwordMessage = ref("");
const passwordError = ref(false);

const sidebarGroups = computed(() => [
  ...navGroups.value.left,
  ...navGroups.value.right,
]);

const searchKeyword = computed(() => searchTerm.value.trim());

const normalizeSearchText = (value = "") => String(value || "").toLocaleLowerCase();

const splitSearchTerms = (keyword = "") =>
  [...new Set(normalizeSearchText(keyword).split(/\s+/).filter(Boolean))];

const escapeHtml = (value = "") =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const escapeRegExp = (value = "") => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const sanitizeRedirectTarget = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw || typeof window === "undefined") {
    return "";
  }

  try {
    const targetUrl = new URL(raw, window.location.origin);
    if (targetUrl.origin !== window.location.origin) {
      return "";
    }

    if (!targetUrl.pathname.startsWith("/")) {
      return "";
    }

    return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
  } catch (error) {
    return "";
  }
};

const loginRedirectTarget = computed(() => sanitizeRedirectTarget(route.query.redirect));

const isVisibleSearchTarget = (item = {}) => {
  if (item.type !== "route") {
    return true;
  }

  const target = String(item.to || "");
  if (target.startsWith("/admin")) {
    return false;
  }

  return true;
};

const getSearchHaystack = (item = {}) =>
  normalizeSearchText([item.title, item.category, item.desc, item.searchText].join(" "));

const scoreSearchItem = (item = {}, terms = []) => {
  const title = normalizeSearchText(item.title);
  const category = normalizeSearchText(item.category);
  const desc = normalizeSearchText(item.desc);
  const searchText = normalizeSearchText(item.searchText);

  return terms.reduce((score, term) => {
    let nextScore = score;

    if (title === term) {
      nextScore += 120;
    }

    if (title.startsWith(term)) {
      nextScore += 70;
    }

    if (title.includes(term)) {
      nextScore += 55;
    }

    if (category.includes(term)) {
      nextScore += 28;
    }

    if (desc.includes(term)) {
      nextScore += 18;
    }

    if (searchText.includes(term)) {
      nextScore += 8;
    }

    return nextScore;
  }, 0);
};

const filteredSearchItems = computed(() => {
  const terms = splitSearchTerms(searchKeyword.value);

  if (!terms.length) {
    return [];
  }

  return searchItems.value
    .filter(isVisibleSearchTarget)
    .filter((item) => {
      const haystack = getSearchHaystack(item);
      return terms.every((term) => haystack.includes(term));
    })
    .map((item) => ({
      ...item,
      searchScore: scoreSearchItem(item, terms),
    }))
    .sort((a, b) => b.searchScore - a.searchScore);
});

const motionLabel = computed(() =>
  backdropPaused.value ? "继续背景旋转" : "暂停背景旋转"
);
const motionTitle = computed(() =>
  backdropPaused.value ? "点击继续背景旋转" : "点击暂停背景旋转"
);

const resultKey = (item) => `${item.category}-${item.title}-${item.to || item.href}`;

const getSearchSnippet = (item = {}) => {
  const terms = splitSearchTerms(searchKeyword.value);
  const sources = [item.desc, item.searchText, item.title].map((source) =>
    String(source || "").replace(/\s+/g, " ").trim()
  );
  const source =
    sources.find((candidate) =>
      terms.some((term) => normalizeSearchText(candidate).includes(term))
    ) ||
    item.desc ||
    item.searchText ||
    "";
  const lowerSource = normalizeSearchText(source);
  const matchIndex = terms.reduce((bestIndex, term) => {
    const index = lowerSource.indexOf(term);
    return index === -1 ? bestIndex : Math.min(bestIndex, index);
  }, Number.POSITIVE_INFINITY);

  if (!Number.isFinite(matchIndex)) {
    return source.slice(0, 130);
  }

  const start = Math.max(matchIndex - 36, 0);
  const end = Math.min(matchIndex + 110, source.length);
  return `${start > 0 ? "..." : ""}${source.slice(start, end)}${end < source.length ? "..." : ""}`;
};

const highlightText = (value = "") => {
  const terms = splitSearchTerms(searchKeyword.value).sort((a, b) => b.length - a.length);
  let html = escapeHtml(value);

  terms.forEach((term) => {
    html = html.replace(new RegExp(`(${escapeRegExp(escapeHtml(term))})`, "gi"), "<mark>$1</mark>");
  });

  return html;
};

const highlightSnippet = (item = {}) => highlightText(getSearchSnippet(item));

const resetPasswordEditor = () => {
  currentPassword.value = "";
  nextPassword.value = "";
  confirmPassword.value = "";
  passwordMessage.value = "";
  passwordError.value = false;
};

const alignDesktopDropdowns = () => {
  const navItems = headerCoreRef.value?.querySelectorAll(".nav-item");
  if (!navItems?.length) {
    return;
  }

  if (window.innerWidth <= 1360) {
    navItems.forEach((item) => {
      item.style.removeProperty("--dropdown-left");
    });
    return;
  }

  navItems.forEach((item) => {
    const trigger = item.querySelector(".nav-trigger");
    const dropdown = item.querySelector(".nav-dropdown");

    if (!trigger || !dropdown) {
      return;
    }

    const triggerStyles = window.getComputedStyle(trigger);
    const dropdownLink = dropdown.querySelector("a");
    const dropdownLinkStyles = dropdownLink ? window.getComputedStyle(dropdownLink) : null;
    const triggerRect = trigger.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const triggerPaddingLeft = Number.parseFloat(triggerStyles.paddingLeft) || 0;
    const dropdownPaddingLeft = dropdownLinkStyles
      ? Number.parseFloat(dropdownLinkStyles.paddingLeft) || 0
      : 0;
    const triggerTextLeft = triggerRect.left - itemRect.left + triggerPaddingLeft;
    const dropdownLeft = triggerTextLeft - dropdownPaddingLeft;

    item.style.setProperty("--dropdown-left", `${dropdownLeft}px`);
  });
};

const syncViewportState = () => {
  const nextCompactViewport = window.innerWidth <= 1360;

  if (nextCompactViewport && !isCompactSidebarViewport.value) {
    sidebarCollapsed.value = true;
    openSidebarGroupKey.value = null;
  }

  if (!nextCompactViewport && isCompactSidebarViewport.value) {
    sidebarCollapsed.value = false;
    openSidebarGroupKey.value = null;
  }

  isCompactSidebarViewport.value = nextCompactViewport;

  if (window.innerWidth > 1360) {
    openNavKey.value = null;
  }

  alignDesktopDropdowns();
};

const handleResize = () => {
  syncViewportState();
};

const containsEventTarget = (container, target, path) => {
  if (!container) {
    return false;
  }

  if (path.includes(container)) {
    return true;
  }

  return Boolean(target && container.contains(target));
};

const handleDocumentPointerDown = (event) => {
  const path = typeof event.composedPath === "function" ? event.composedPath() : [];
  const target = event.target instanceof Node ? event.target : null;

  if (
    !containsEventTarget(searchPanelRef.value, target, path) &&
    !containsEventTarget(searchToggleRef.value, target, path)
  ) {
    searchOpen.value = false;
  }

  if (
    !containsEventTarget(userPanelRef.value, target, path) &&
    !containsEventTarget(userToggleRef.value, target, path)
  ) {
    userMenuOpen.value = false;
  }

  if (
    !sidebarCollapsed.value &&
    !containsEventTarget(responsiveSidebarRef.value, target, path) &&
    !containsEventTarget(sidebarToggleRef.value, target, path)
  ) {
    openSidebarGroupKey.value = null;
    sidebarCollapsed.value = true;
  }

  const elementTarget = event.target instanceof Element ? event.target : null;
  if (window.innerWidth <= 1360 && !elementTarget?.closest(".nav-item")) {
    openNavKey.value = null;
  }
};

const toggleSearch = async () => {
  searchOpen.value = !searchOpen.value;
  userMenuOpen.value = false;

  if (searchOpen.value) {
    await nextTick();
    searchInputRef.value?.focus();
  }
};

const closeSearch = () => {
  searchOpen.value = false;
};

const submitSearch = () => {
  const firstResult = filteredSearchItems.value[0];

  if (!firstResult) {
    return;
  }

  closeSearch();

  if (firstResult.type === "route" && firstResult.to) {
    router.push(firstResult.to);
    return;
  }

  if (firstResult.href) {
    window.open(firstResult.href, "_blank", "noopener,noreferrer");
  }
};

const toggleUserMenu = () => {
  userMenuOpen.value = !userMenuOpen.value;
  searchOpen.value = false;
  loginError.value = "";
  passwordMessage.value = "";
  passwordError.value = false;
};

const closeUserMenu = () => {
  userMenuOpen.value = false;
};

const syncLoginPromptState = () => {
  if (String(route.query.login || "") !== "1" || isLoggedIn.value) {
    return;
  }

  userMenuOpen.value = true;
  searchOpen.value = false;
  loginError.value = "";
  passwordMessage.value = "";
  passwordError.value = false;
};

const toggleNav = (id) => {
  if (window.innerWidth > 1360) {
    return;
  }

  openNavKey.value = openNavKey.value === id ? null : id;
};

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
  if (sidebarCollapsed.value) {
    openSidebarGroupKey.value = null;
  }
};

const toggleSidebarGroup = (id) => {
  if (sidebarCollapsed.value) {
    return;
  }

  openSidebarGroupKey.value = openSidebarGroupKey.value === id ? null : id;
};

const openSidebarLinks = (id) => {
  if (sidebarCollapsed.value) {
    return;
  }

  openSidebarGroupKey.value = id;
};

const closeSidebarLinks = (id) => {
  if (openSidebarGroupKey.value !== id) {
    return;
  }

  openSidebarGroupKey.value = null;
};

const toggleMotion = (event) => {
  const button = event.currentTarget;

  backdropPaused.value = !backdropPaused.value;

  if (!(button instanceof HTMLElement)) {
    return;
  }

  button.classList.remove("is-pressed");
  void button.offsetWidth;
  button.classList.add("is-pressed");

  window.setTimeout(() => {
    button.classList.remove("is-pressed");
  }, 180);
};

const submitLogin = async () => {
  const result = await login(loginUsername.value, loginPassword.value);
  if (!result.ok) {
    loginError.value = result.error;
    return;
  }

  loginError.value = "";
  loginPassword.value = "";
  loginPasswordVisible.value = false;
  passwordEditorOpen.value = false;
  await loadContent();

  if (loginRedirectTarget.value && typeof window !== "undefined") {
    window.location.assign(loginRedirectTarget.value);
    return;
  }

  if (route.query.login || route.query.redirect) {
    const nextQuery = { ...route.query };
    delete nextQuery.login;
    delete nextQuery.redirect;
    await router.replace({
      path: route.path,
      query: nextQuery,
    });
  }

  closeUserMenu();
};

const submitPasswordChange = async () => {
  const result = await changeOwnPassword({
    currentPassword: currentPassword.value,
    nextPassword: nextPassword.value,
    confirmPassword: confirmPassword.value,
  });

  passwordMessage.value = result.message || result.error || "";
  passwordError.value = !result.ok;

  if (result.ok) {
    resetPasswordEditor();
    passwordEditorOpen.value = false;
  }
};

const handleLogout = async () => {
  await logout();
  try {
    await fetch("/gupiao/api/session", {
      method: "DELETE",
      credentials: "same-origin",
    });
  } catch (error) {
    // Ignore stock-intel session cleanup failures during logout.
  }
  passwordEditorOpen.value = false;
  resetPasswordEditor();
  await loadContent();
  closeUserMenu();

  if (route.name === "admin" || route.name === "sound-blank") {
    router.push("/");
  }
};

watch(
  sidebarCollapsed,
  (collapsed) => {
    document.body.classList.toggle("sidebar-collapsed", collapsed);
    if (collapsed) {
      openSidebarGroupKey.value = null;
    }
  },
  { immediate: true }
);

watch(
  backdropPaused,
  (paused) => {
    document.body.classList.toggle("backdrop-paused", paused);
  },
  { immediate: true }
);

watch(
  sidebarGroups,
  async (groups) => {
    const exists = groups.some((group) => group.id === openSidebarGroupKey.value);
    if (!exists) {
      openSidebarGroupKey.value = null;
    }

    await nextTick();
    alignDesktopDropdowns();
  },
  { deep: true }
);

watch(
  () => [route.query.login, route.query.redirect, isLoggedIn.value],
  () => {
    syncLoginPromptState();
  },
  { immediate: true }
);

watch(
  () => route.fullPath,
  () => {
    closeSearch();
    closeUserMenu();
    openNavKey.value = null;
    openSidebarGroupKey.value = null;
    if (isCompactSidebarViewport.value) {
      sidebarCollapsed.value = true;
    }
  }
);

onMounted(async () => {
  ensureSessionLoaded();
  document.addEventListener("pointerdown", handleDocumentPointerDown);
  window.addEventListener("resize", handleResize);

  await nextTick();
  syncViewportState();
  syncLoginPromptState();
});

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", handleDocumentPointerDown);
  window.removeEventListener("resize", handleResize);
  document.body.classList.remove("sidebar-collapsed", "backdrop-paused");
});
</script>
